import { startTransition } from "react";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { detectLang, loadLocale } from "./i18n/detect";

// Error monitoring is loaded lazily (see below), so buffer anything thrown
// before it exists: locale pack load, the initial mount, early interactions.
const earlyErrors: unknown[] = [];
const onEarlyError = (e: ErrorEvent) => {
  earlyErrors.push(e.error ?? new Error(e.message));
};
const onEarlyRejection = (e: PromiseRejectionEvent) => {
  earlyErrors.push(e.reason);
};
window.addEventListener("error", onEarlyError);
window.addEventListener("unhandledrejection", onEarlyRejection);

const lang = detectLang();
document.documentElement.lang = lang;
document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";

// Non-English translations live in their own chunk; wait for it before the
// first render so nothing flashes English. English resolves immediately.
loadLocale(lang)
  .then((pack) => {
    if (pack.lang !== lang) {
      // The chunk for the detected language failed to load; the page renders
      // in English, and the failure is reported once monitoring is up.
      earlyErrors.push(new Error(`Locale pack "${lang}" failed to load, fell back to English`));
      document.documentElement.lang = pack.lang;
      document.documentElement.dir = "ltr";
    }
    const root = createRoot(document.getElementById("root")!);
    // Mount as a transition: React then renders in interruptible slices and
    // yields to taps instead of blocking the main thread for the whole mount.
    // The prerendered HTML stays visible until the mount commits.
    startTransition(() => root.render(<App />));
  })
  .catch((err) => {
    earlyErrors.push(err);
  });

// Error monitoring (see src/monitoring.ts) is loaded once the page is idle so
// its code never competes with hydration or the visitor's first taps.
function loadErrorMonitoring() {
  import("./monitoring")
    .then((m) => {
      window.removeEventListener("error", onEarlyError);
      window.removeEventListener("unhandledrejection", onEarlyRejection);
      m.initErrorMonitoring(earlyErrors.splice(0));
    })
    .catch(() => {
      /* monitoring is best-effort */
    });
}

function scheduleErrorMonitoring() {
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(loadErrorMonitoring, { timeout: 5000 });
  } else {
    setTimeout(loadErrorMonitoring, 3000);
  }
}

if (document.readyState === "complete") {
  scheduleErrorMonitoring();
} else {
  window.addEventListener("load", scheduleErrorMonitoring, { once: true });
}
